import { Link, useLocation } from "react-router-dom";
import { MapPin, Clock, Facebook, Instagram, Mail } from "lucide-react";

export default function Footer() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin") && location.pathname !== "/admin/login") {
    return null;
  }
  const hideOn = ["/portal", "/signup", "/login", "/verify"];
  if (hideOn.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
    return null;
  }

  return (
    <footer className="relative bg-black border-t border-white/5" data-testid="main-footer">
      <div className="absolute inset-0 lightning-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <img src="/logo.png" alt="The Push Center" className="w-12 h-12 rounded-full ring-1 ring-electric/40" />
            <div>
              <div className="font-display font-black text-xl text-white tracking-tight">THE PUSH CENTER</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-electric">Into Your Destiny</div>
            </div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-xs">
            Building people. Strengthening faith. Creating a safe space where all can grow and belong.
          </p>
          <div className="flex gap-3 mt-6">
            <a
              href="https://www.facebook.com/share/1By8zRYFPB/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-300 hover:text-electric hover:border-electric transition-all"
              data-testid="footer-facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/pushcenterva"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-300 hover:text-electric hover:border-electric transition-all"
              data-testid="footer-instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-4">Visit</div>
          <div className="flex items-start gap-3 text-sm text-zinc-300">
            <MapPin className="w-4 h-4 mt-1 text-electric flex-shrink-0" />
            <div>
              6217 Portsmouth Blvd<br />Portsmouth, VA 23701
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-4">Service Times</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-electric" /> Sundays · 9:00 AM</li>
            <li className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-electric" /> Bible Study · Wed 7:30 PM</li>
            <li className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-electric" /> Sunday School · 1st Sun 8:00 AM</li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-4">Links</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-about">About</Link></li>
            <li><Link to="/ministries" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-ministries">Ministries</Link></li>
            <li><Link to="/blog" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-blog">Blog</Link></li>
            <li><Link to="/new-members" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-newmembers">New Members</Link></li>
            <li><Link to="/booking" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-booking">Book A Space</Link></li>
            <li><Link to="/giving" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-give">Give</Link></li>
            <li><Link to="/contact" className="text-zinc-300 hover:text-electric transition" data-testid="footer-link-contact">Contact</Link></li>
            <li className="pt-2 border-t border-white/5"><Link to="/login" className="text-electric hover:underline transition text-xs font-bold uppercase tracking-wider" data-testid="footer-link-member-login">Member Login</Link></li>
            <li><Link to="/signup" className="text-zinc-300 hover:text-electric transition text-xs" data-testid="footer-link-signup">Create Account</Link></li>
            <li><Link to="/admin/login" className="text-zinc-500 hover:text-electric transition text-xs" data-testid="footer-link-admin">Staff Login</Link></li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} The Push Center. All rights reserved.</div>
          <div className="flex items-center gap-1.5">
            Vision Brought To Life By{" "}
            <a
              href="https://vzngfx.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-electric hover:text-white font-bold transition"
              data-testid="footer-vzngfx-link"
            >
              VZNGFX
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
